import {
  SupplierRelationshipList_retailersParameterModifierType,
  SupplierRelationshipList_retailersSortModifierType,
} from "@sellar-io/api-v2/types";
import { PaginationState, SortingState } from "@tanstack/react-table";
import { create } from "zustand";
import { querystring } from "zustand-querystring";
import { FilterType, SortType } from "../../../lists/types";
import { CustomerListSearchType } from "../../customer/customerList/CustomerListFilterTypes";

export type BuyerRelationshipsSortType =
  | "name_asc"
  | "name_desc"
  | "last_ordered_desc"
  | "last_ordered_asc"
  | "storefront_release_date_asc";

export type BuyerRelationshipsStoreState = {
  columnSortName: BuyerRelationshipsSortType;
  page: number;
  perPage: number;
  scrollOffset: number;
  sorting: SortingState;
  pagination: PaginationState;
};

type BuyerRelationshipsStoreActions = {
  setColumnSortName: (columnSort: BuyerRelationshipsSortType) => void;
  setSorting: (sorting: SortingState) => void;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  setScrollOffset: (scrollOffset: number) => void;
  reset: () => void;
  setPagination: (pagination: PaginationState) => void;
};

const initialBuyerRelationshipsStoreState: BuyerRelationshipsStoreState = {
  columnSortName: "name_asc",
  page: 1,
  perPage: 25,
  scrollOffset: 0,
  pagination: {
    pageIndex: 0,
    pageSize: 25,
  },
  sorting: [
    {
      id: "name",
      desc: false,
    },
  ],
};

export const BuyerRelationshipsSearchValues: FilterType<
  SupplierRelationshipList_retailersParameterModifierType,
  CustomerListSearchType
> = {
  name: {
    key: "name",
    name: "Name",
    mutation: (value: string) => [
      {
        type: "name",
        modifier: "INCLUDES",
        value,
      },
    ],
  },
  postalCode: {
    key: "postalCode",
    name: "Postcode",
    mutation: (value: string) => [
      {
        type: "postalCode",
        modifier: "INCLUDES",
        value,
      },
    ],
  },
};

export const BuyerRelationshipsSortValues: SortType<
  SupplierRelationshipList_retailersSortModifierType,
  BuyerRelationshipsSortType
> = {
  name_asc: {
    key: "name_asc",
    name: "Supplier name (A-Z)",
    mutation: {
      type: "name",
      direction: "asc",
    },
  },
  name_desc: {
    key: "name_desc",
    name: "Supplier name (Z-A)",
    mutation: {
      type: "name",
      direction: "desc",
    },
  },
  last_ordered_desc: {
    key: "last_ordered_desc",
    name: "Last ordered (Newest - Oldest)",
    mutation: {
      type: "lastOrderedAt",
      direction: "desc",
    },
  },
  last_ordered_asc: {
    key: "last_ordered_asc",
    name: "Last ordered (Oldest - Newest)",
    mutation: {
      type: "lastOrderedAt",
      direction: "asc",
    },
  },
  storefront_release_date_asc: {
    key: "storefront_release_date_asc",
    name: "Storefront release date (Oldest - Newest)",
    mutation: {
      type: "storefrontReleaseDate",
      direction: "asc",
    },
  },
};

type BuyerRelationshipsStoreType = BuyerRelationshipsStoreState &
  BuyerRelationshipsStoreActions;

const migrateBuyerRelationshipsQueryStringState = () => {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  const legacyState = url.searchParams.get("RetailerRelationshipsState");

  if (legacyState === null) return;

  // Preserve bookmarked links while ensuring all future store updates use the
  // canonical Buyer key. If both keys exist, the canonical value wins.
  if (!url.searchParams.has("BuyerRelationshipsState")) {
    url.searchParams.set("BuyerRelationshipsState", legacyState);
  }
  url.searchParams.delete("RetailerRelationshipsState");
  window.history.replaceState(window.history.state, "", url);
};

migrateBuyerRelationshipsQueryStringState();

export const useBuyerRelationshipsStore = create<BuyerRelationshipsStoreType>()(
  querystring<BuyerRelationshipsStoreType>(
    (set) => ({
      ...initialBuyerRelationshipsStoreState,
      // setState: (newState: BuyerRelationshipsStoreState) => set(newState),

      setColumnSortName: (columnSortName: BuyerRelationshipsSortType) =>
        set({ columnSortName }),
      setPagination: (pagination: PaginationState) => set({ pagination }),
      setSorting: (sorting: SortingState) => set({ sorting }),
      setPage: (page: number) => set({ page }),
      setPerPage: (perPage: number) => set({ perPage }),
      setScrollOffset: (scrollOffset: number) => set({ scrollOffset }),

      reset: () => set(initialBuyerRelationshipsStoreState),
    }),
    {
      select(pathname) {
        const persistParam = pathname.includes("/suppliers");
        return {
          columnSortName: persistParam,
          page: persistParam,
          perPage: persistParam,
          scrollOffset: persistParam,
          sorting: persistParam,
          pagination: persistParam,
        };
      },
      key: "BuyerRelationshipsState",
    },
  ),
);
